import { Capacitor } from '@capacitor/core';
import api from '../api/client';

// Push notification client wrapper (docs/TODO.md §1a). Architecture confirmed against
// ~/Developer/Currently's own working, real-device-tested implementation: FCM end-to-end
// on both platforms via @capacitor-firebase/messaging, so the server needs exactly one send
// path (server/services/pushTransportFcm.js). Unlike Currently — which writes tokens
// straight to Firestore under security rules — this posts/deletes against Agence's existing
// Postgres-backed /api/v1/push/tokens endpoints, since there is no Firestore here.
//
// The plugin is dynamically imported so a plain web build never pulls native Firebase
// Messaging code into the bundle — mirrors nativeSocialAuth.js's own pattern.

const REGISTERED_TOKEN_KEY = 'agence_push_token';
const PROMPTED_KEY = 'agence_push_prompted';

// Ported from ~/Developer/Currently's identical fix (project-memory/history/push-notifications.md
// there) after hitting the exact same failure on-device (2026-09-11):
//   Error: The operation couldn't be completed. No APNS token specified before fetching FCM Token
// getToken() can race ahead of the APNs handshake that requestPermissions() kicks off — iOS's
// device-token round trip to Apple's servers is asynchronous and not guaranteed to finish before
// requestPermissions() resolves 'granted'. 5s is generous; Apple's servers normally respond in
// under a second on a real network. Never rejects — a slow/missing handshake degrades to "try
// getToken() anyway" rather than blocking registration forever.
const APNS_TOKEN_WAIT_MS = 5000;

// Set once the native APNs handshake is known to have completed this app session — either
// apnsTokenReceived actually fired, or getToken() already succeeded once (which can only happen
// after the handshake completes natively, whether or not the JS event fired for it). Currently's
// own device log confirmed apnsTokenReceived does NOT reliably fire again on every call within a
// warm session — without this guard, every re-registration (e.g. toggling the Settings checkbox
// off then on) would pay the full APNS_TOKEN_WAIT_MS wait for an event that isn't coming again.
let hasCompletedApnsHandshakeThisSession = false;

export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

// Wrapped in a plain object on purpose — NEVER return a Capacitor plugin proxy directly from
// an async function or resolve a promise with it. The proxy answers any property access with
// a function that forwards to native, INCLUDING `then`, so the promise machinery sees a
// thenable and calls `FirebaseMessaging.then(resolve, reject)`. That forwards to native as a
// bogus "then" method call, which rejects into an unrelated, never-awaited promise (a silent
// unhandled rejection, invisible in Xcode's console — only visible via Safari Web Inspector) —
// while resolve/reject for the REAL await are never invoked, so every caller hangs forever.
// This was the actual root cause of "push toggle/Face ID never resolves" (2026-09-11),
// previously misdiagnosed as a Firebase-configuration and entitlement issue. Same bug, same
// fix, as nativeSocialAuth.js's ensureGoogleInitialized() — see its comment for the full
// mechanism. Pinned by pushNotifications.test.js.
async function loadPlugin() {
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
  return { plugin: FirebaseMessaging };
}

/**
 * Android requires an explicit notification channel before notifications display correctly
 * on modern versions (app-store-playbook: "guard this code so it only runs on Android, not
 * iOS") — iOS has no equivalent concept and errors if called.
 */
export async function ensureAndroidNotificationChannel() {
  if (!isNativePlatform() || Capacitor.getPlatform() !== 'android') return;
  const { plugin: FirebaseMessaging } = await loadPlugin();
  await FirebaseMessaging.createChannel({
    id: 'default',
    name: 'Agence',
    description: 'Alerts about your budget, portfolio, and automations',
    importance: 4, // High
    visibility: 1, // Public
    lights: true,
    lightColor: '#0f172a',
    vibration: true,
  });
}

export async function checkPushPermission() {
  if (!isNativePlatform()) return 'prompt';
  const { plugin: FirebaseMessaging } = await loadPlugin();
  const { receive } = await FirebaseMessaging.checkPermissions();
  return receive;
}

/**
 * Whether this device completed registration in a past session — read from localStorage so
 * the Settings-page toggle can render without a server round trip. Distinct from OS
 * permission state: a user can have OS permission granted but have disabled the toggle (or
 * vice versa, if permission was later revoked in system Settings).
 */
export function isRegisteredOnThisDevice() {
  return !!localStorage.getItem(REGISTERED_TOKEN_KEY);
}

/** Whether the one-time post-onboarding prompt has already fired on this device. */
export function hasPromptedOnThisDevice() {
  return !!localStorage.getItem(PROMPTED_KEY);
}

async function saveToken(token) {
  const platform = Capacitor.getPlatform();
  await api.post('/push/tokens', { token, platform });
  localStorage.setItem(REGISTERED_TOKEN_KEY, token);
}

/**
 * Resolves once iOS's `apnsTokenReceived` fires, or after APNS_TOKEN_WAIT_MS, whichever comes
 * first. No-op (resolves immediately) on Android/web, or once the handshake is already known
 * to have completed this session.
 */
async function waitForApnsToken() {
  if (Capacitor.getPlatform() !== 'ios' || hasCompletedApnsHandshakeThisSession) return;
  const { plugin: FirebaseMessaging } = await loadPlugin();
  await new Promise(resolve => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve();
      }
    }, APNS_TOKEN_WAIT_MS);
    FirebaseMessaging.addListener('apnsTokenReceived', () => {
      hasCompletedApnsHandshakeThisSession = true;
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve();
    }).then(handle => {
      // Already resolved (via the timeout) by the time the listener finished registering —
      // nothing left listening for, clean it up.
      if (settled) handle.remove();
    });
  });
}

/**
 * Requests OS permission and registers this device's FCM token with the server. Safe to call
 * more than once — requestPermissions() is idempotent once the OS has recorded an answer, it
 * will not re-show the native dialog (confirmed against Currently's own production behavior,
 * five separate registration attempts including a full delete-and-reinstall).
 */
export async function registerForPushNotifications() {
  if (!isNativePlatform()) return 'prompt';
  const { plugin: FirebaseMessaging } = await loadPlugin();
  const { receive } = await FirebaseMessaging.requestPermissions();
  // Only mark this device as prompted once iOS has actually recorded a real answer —
  // confirmed real bug: this used to run before the plugin call, so a single native
  // failure permanently suppressed the auto-prompt-on-login retry on every future launch,
  // even though iOS never actually recorded a decision (the app never appeared in
  // Settings > Notifications at all).
  localStorage.setItem(PROMPTED_KEY, '1');
  if (receive !== 'granted') return receive;
  await waitForApnsToken();
  const { token } = await FirebaseMessaging.getToken();
  hasCompletedApnsHandshakeThisSession = true;
  await saveToken(token);
  return receive;
}

/** Deregisters this device — call on sign-out or when the user disables push in Settings. */
export async function unregisterPushNotifications() {
  if (!isNativePlatform()) return;
  const token = localStorage.getItem(REGISTERED_TOKEN_KEY);
  const { plugin: FirebaseMessaging } = await loadPlugin();
  await FirebaseMessaging.deleteToken();
  localStorage.removeItem(REGISTERED_TOKEN_KEY);
  if (token) {
    await api.delete('/push/tokens', { data: { token } }).catch(() => {});
  }
}

/** Re-saves the token on FCM's own rotation event so the server never holds a stale one. */
export function subscribeToPushTokenRefresh() {
  if (!isNativePlatform()) return () => {};
  let handle;
  loadPlugin()
    .then(({ plugin: FirebaseMessaging }) => FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
      saveToken(token).catch(() => {});
    }))
    .then(h => { handle = h; })
    .catch(() => {});
  return () => handle?.remove();
}

/** Routes a tapped notification's data payload to the caller (e.g. a router navigate call). */
export function subscribeToNotificationTaps(onTap) {
  if (!isNativePlatform()) return () => {};
  let handle;
  loadPlugin()
    .then(({ plugin: FirebaseMessaging }) => FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      onTap(event?.notification?.data || {});
    }))
    .then(h => { handle = h; })
    .catch(() => {});
  return () => handle?.remove();
}
